import React from "react";
import styled from "@emotion/styled";

import { FeatureType } from "components/paywall/paywallUtils";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { EventNames, useTracking } from "lib/tracking";
import { DataTypeNameWithBuyersAndSuppliers } from "lib/types/models";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "../../../lib/themes/colors";
import { Flex, Text } from "../../../styles/utility-components";
import Button from "../../ui/button/Button";
import { getEntityLabel } from "../utils";
import PaidTag from "./PaidTag";
import { Entities, entityToDataType } from "./utils";

import AddEntitySvg from "../../../../assets//images/add_entity.svg";

type Props = {
  entity: Entities;
  url: string;
  isEmptyState?: boolean;
  showAddEntity?: boolean;
};

export const getEntityEmptyStateDescription = (entity: Entities): string => {
  switch (entity) {
    case "notice":
      return "By adding a notice, you can generate insights from past procurement notices related to this notice, helping you to qualify the opportunity and gather valuable information for outreach.";
    case "document":
      return "Uncover strategic priorities by exploring procurement strategies, analysing budgets, and aligning with the buyer's social policies to position your solution effectively.";
    case "framework":
      return "Leverage frameworks to unlock new opportunities by checking applicability, assessing eligibility for direct or partnered access, and understanding buyer preferences and routes to market.";
    case "buyer":
      return "Better qualify opportunities by evaluating past awards, analysing deal values, and leveraging your history with the buyer to deepen your understanding.";
    case "supplier":
      return "Add relevant suppliers to assess their relationships with the buyer and leverage your network for insights and potential introductions.";
    case "contact":
      return "Identify key decision-makers, start conversations, book meetings to understand their priorities, and build strong relationships to influence the buying process and align your solution.";
  }
};

const dataTypeToPaywall = (dataType: DataTypeNameWithBuyersAndSuppliers): FeatureType | null => {
  // Not including BUYERS since the core feature is free which includes adding buyers to an opportunity
  if (["DOCUMENTS", "FRAMEWORKS", "SUPPLIERS", "CONTACTS"].includes(dataType)) {
    return dataType as FeatureType;
  }
  return null;
};

export default function FindEntity({ entity, url, isEmptyState, showAddEntity = true }: Props) {
  const { logEvent } = useTracking();
  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();

  const dataType = entityToDataType(entity);
  const hasDataType = subscription.hasDataTypes(dataType);
  const paywall = dataTypeToPaywall(dataType);
  const hasPaywall = paywall && !hasDataType;

  const onClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (hasPaywall) {
      e.preventDefault();
      openPaywallModal(paywall);
    } else {
      logEvent(EventNames.addOpportunityDataClicked, {
        "Data type": entity.toUpperCase(),
      });
    }
  };

  const shouldShowAddEntity = showAddEntity && !isEmptyState;

  if (shouldShowAddEntity) {
    return hasPaywall ? (
      <Button as="button" buttonType="secondary" onClick={onClick} icon="plus" fullWidth>
        {getEntityLabel(entity)}
      </Button>
    ) : (
      <Button as="a" href={url} buttonType="secondary" onClick={onClick} icon="plus" fullWidth>
        {getEntityLabel(entity)}
      </Button>
    );
  }

  return isEmptyState ? (
    <StyledLink
      href={hasPaywall ? undefined : url}
      onClick={onClick}
      applyLinkStyle={!isEmptyState}
    >
      <Card>
        <Flex width="100%" justifyContent="space-between" alignItems="flex-start">
          <Flex gap={12} alignItems="center">
            <img src={AddEntitySvg} alt="Find entity" height={82} />
            <Description>
              <Text bold>Find {getEntityLabel(entity, false)}</Text>
              <Text description>{getEntityEmptyStateDescription(entity)}</Text>
            </Description>
          </Flex>
          {hasPaywall ? (
            <PaidTag />
          ) : (
            <IconWrapper>
              <UIcon icon="plus" color={sysPrimaryDefault} />
            </IconWrapper>
          )}
        </Flex>
      </Card>
    </StyledLink>
  ) : null;
}

const Card = styled(Flex)(({ theme }) => ({
  display: "flex",
  width: "100%",
  padding: 15,
  borderRadius: 8,
  backgroundImage: `repeating-linear-gradient(90deg, ${theme.colors.sysBorderPrimary}, ${theme.colors.sysBorderPrimary} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysBorderPrimary}, ${theme.colors.sysBorderPrimary} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(90deg, ${theme.colors.sysBorderPrimary}, ${theme.colors.sysBorderPrimary} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysBorderPrimary}, ${theme.colors.sysBorderPrimary} 12px, transparent 12px, transparent 28px)`,
  backgroundPosition: "left top, right top, left bottom, left top",
  backgroundRepeat: "repeat-x, repeat-y, repeat-x, repeat-y",
  backgroundSize: "100% 1px, 1px 100%, 100% 1px, 1px 100%",
  transition: "all 0.2s ease-in-out",
}));

const StyledLink = styled.a<{ applyLinkStyle?: boolean }>(({ theme, applyLinkStyle }) => ({
  all: "unset",
  cursor: "pointer",
  display: "block",
  width: "100%",
  color: applyLinkStyle ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
  "&:hover": {
    color: applyLinkStyle ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
    [`${Card}`]: {
      backgroundColor: theme.colors.sysPrimarySubtle,
      backgroundImage: `repeating-linear-gradient(90deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(90deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px), repeating-linear-gradient(180deg, ${theme.colors.sysPrimaryDefault}, ${theme.colors.sysPrimaryDefault} 12px, transparent 12px, transparent 28px)`,
    },
  },
}));

const Description = styled(Flex)({
  flexDirection: "column",
  gap: 4,
});

const IconWrapper = styled(Flex)({
  padding: "5px 8px",
});
